/**
 * Organisation-level settings (admin only).
 *
 * Phase 8 will grow this with users, roles, security, etc.
 * Right now it's focused on integrations (Aircall + Outlook server config).
 */
import { useState } from "react"
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import { Link } from "react-router-dom"
import {
  Phone,
  Mail,
  CheckCircle2,
  XCircle,
  Loader2,
  AlertTriangle,
  Trash2,
  Upload,
  Download,
} from "lucide-react"
import { AppShell } from "@/components/AppShell"
import { Card } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Badge } from "@/components/ui/badge"
import { integrationsApi, ApiError } from "@/lib/api"
import { useAuth } from "@/hooks/useAuth"
import { timeAgo } from "@shared/utils/date"

export default function SettingsPage() {
  const { user } = useAuth()
  const isAdmin = user?.role === "admin"

  const status = useQuery({
    queryKey: ["integration-status"],
    queryFn: () => integrationsApi.status(),
  })

  return (
    <AppShell title="Settings" subtitle="Organisation settings & integrations">
      <div className="max-w-3xl space-y-6">
        {/* Access notice */}
        {!isAdmin && (
          <Card className="border-amber-200 bg-amber-50 p-4">
            <div className="flex gap-3 text-sm text-amber-900">
              <AlertTriangle className="h-5 w-5 shrink-0" />
              <div>
                Only <strong>admins</strong> can change organisation integrations. You can still view
                the current status here.
              </div>
            </div>
          </Card>
        )}

        {/* Aircall */}
        <AircallCard
          status={status.data ?? null}
          isLoading={status.isLoading}
          canEdit={isAdmin}
        />

        {/* Outlook server config */}
        <OutlookOrgCard status={status.data ?? null} isLoading={status.isLoading} />

        {/* Data Import */}
        <ImportCard canImport={isAdmin || user?.role === "manager"} />
      </div>
    </AppShell>
  )
}

function ImportCard({ canImport }: { canImport: boolean }) {
  return (
    <Card className="p-6">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="bg-rc-blue-100 text-rc-blue-700 grid h-10 w-10 place-items-center rounded-lg">
            <Upload className="h-5 w-5" />
          </div>
          <div>
            <h3 className="text-base font-semibold text-foreground">Data Import</h3>
            <p className="text-muted-foreground text-sm">
              Bulk import contacts + previous/current/future deals from a CSV or XLSX file.
            </p>
          </div>
        </div>
      </div>
      <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Link
          to={"/import?mode=contacts"}
          className={`border-border rounded-md border p-4 transition-colors hover:border-rc-blue-400 ${
            canImport ? "" : "pointer-events-none opacity-50"
          }`}
        >
          <div className="flex items-center gap-2">
            <Upload className="text-rc-blue-600 h-4 w-4" />
            <span className="font-medium text-foreground">Import contacts</span>
          </div>
          <p className="text-muted-foreground mt-1 text-xs">
            Just contact records — no deal history.
          </p>
        </Link>
        <Link
          to={"/import?mode=deals"}
          className={`border-border rounded-md border p-4 transition-colors hover:border-rc-blue-400 ${
            canImport ? "" : "pointer-events-none opacity-50"
          }`}
        >
          <div className="flex items-center gap-2">
            <Download className="text-rc-blue-600 h-4 w-4" />
            <span className="font-medium text-foreground">Import deals + contacts</span>
          </div>
          <p className="text-muted-foreground mt-1 text-xs">
            Aggregator export with applicant, business, and deal fields (previous, current, future).
          </p>
        </Link>
      </div>
      {!canImport && (
        <p className="text-muted-foreground mt-3 text-xs">
          Only admin and manager roles can import data.
        </p>
      )}
    </Card>
  )
}

// ============================================================
// Aircall card
// ============================================================
function AircallCard({
  status,
  isLoading,
  canEdit,
}: {
  status: Awaited<ReturnType<typeof integrationsApi.status>> | null
  isLoading: boolean
  canEdit: boolean
}) {
  const qc = useQueryClient()
  const [apiId, setApiId] = useState("")
  const [apiToken, setApiToken] = useState("")
  const [workspaceName, setWorkspaceName] = useState("")
  const [testResult, setTestResult] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  const connected = status?.org.aircall?.isActive === true

  const connect = useMutation({
    mutationFn: () =>
      integrationsApi.connectAircall({
        apiId: apiId.trim(),
        apiToken: apiToken.trim(),
        workspaceName: workspaceName.trim() || undefined,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["integration-status"] })
      setApiId("")
      setApiToken("")
      setError(null)
    },
    onError: (err) =>
      setError(err instanceof ApiError ? err.message : "Failed to save credentials"),
  })

  const disconnect = useMutation({
    mutationFn: () => integrationsApi.disconnectAircall(),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["integration-status"] }),
  })

  const test = useMutation({
    mutationFn: () => integrationsApi.testAircall(),
    onSuccess: (r) =>
      setTestResult(
        r.ok ? "✓ Connection to Aircall verified" : `Test failed: ${r.message ?? "unknown"}`
      ),
    onError: (err) =>
      setTestResult(err instanceof Error ? `Test failed: ${err.message}` : "Test failed"),
  })

  type SyncResult = Awaited<ReturnType<typeof integrationsApi.syncAircallUsers>>
  const [syncResult, setSyncResult] = useState<SyncResult | null>(null)
  const sync = useMutation({
    mutationFn: () => integrationsApi.syncAircallUsers(),
    onSuccess: (r) => {
      setSyncResult(r)
      qc.invalidateQueries({ queryKey: ["integration-status"] })
    },
    onError: (err) =>
      setError(err instanceof ApiError ? err.message : "Failed to sync users"),
  })

  const [copied, setCopied] = useState(false)
  const copyWebhookUrl = () => {
    const u = status?.aircallWebhookUrl
    if (!u) return
    void navigator.clipboard.writeText(u)
    setCopied(true)
    window.setTimeout(() => setCopied(false), 1500)
  }

  const webhookConfigured = status?.envConfigured.aircall.webhookSecret === true

  return (
    <Card className="p-6">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="grid h-10 w-10 place-items-center rounded-lg bg-emerald-100 text-emerald-700">
            <Phone className="h-5 w-5" />
          </div>
          <div>
            <h3 className="text-base font-semibold text-foreground">Aircall</h3>
            <p className="text-muted-foreground text-sm">
              Log incoming + outgoing calls to contact timelines.
            </p>
          </div>
        </div>
        <StatusPill loading={isLoading} connected={connected} />
      </div>

      {connected ? (
        <div className="mt-4 space-y-3">
          <div className="text-sm">
            Connected as <strong>{status?.org.aircall?.displayName ?? "Aircall"}</strong>
            {status?.org.aircall?.updatedAt && (
              <span className="text-muted-foreground">
                {" "}
                · updated {timeAgo(status.org.aircall.updatedAt)}
              </span>
            )}
          </div>

          <div className="bg-rc-grey-50 rounded-md border border-border p-3">
            <div className="flex items-start justify-between gap-2">
              <div className="text-xs font-medium text-foreground">Webhook URL (add this to Aircall)</div>
              <button
                onClick={copyWebhookUrl}
                className="text-xs text-muted-foreground hover:text-foreground underline"
                type="button"
              >
                {copied ? "Copied ✓" : "Copy"}
              </button>
            </div>
            <code className="mt-1 block break-all text-xs">
              {status?.aircallWebhookUrl ?? "…"}
            </code>
            <p className="text-muted-foreground mt-2 text-xs">
              Point Aircall at this URL directly (not the Pages proxy). Subscribe to
              events: <em>call.ended</em>, <em>call.comm_assets_generated</em>,
              <em> message.received</em>, <em>message.sent</em>. Signature:
              HMAC-SHA256(<em>AIRCALL_WEBHOOK_SECRET</em>, raw body) sent as
              <code>X-Aircall-Signature</code>.
            </p>
          </div>

          {/* Broker mapping (Aircall user -> CRM user) */}
          <div className="bg-rc-grey-50 rounded-md border border-border p-3">
            <div className="flex items-center justify-between gap-2">
              <div>
                <div className="text-xs font-medium text-foreground">Broker mapping</div>
                <p className="text-muted-foreground text-xs">
                  Match each broker to their Aircall user + number so inbound calls and
                  SMS attribute correctly. Matches by email.
                </p>
              </div>
              {canEdit && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => sync.mutate()}
                  disabled={sync.isPending}
                >
                  {sync.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                  Sync Aircall users
                </Button>
              )}
            </div>

            {syncResult && (
              <div className="mt-3 space-y-2 text-xs">
                <div className="text-foreground">
                  ✓ {syncResult.totals.matched} matched ·{" "}
                  {syncResult.totals.unmatchedAircall} Aircall users without a CRM broker ·{" "}
                  {syncResult.totals.unmatchedBrokers} CRM brokers without an Aircall user
                </div>
                {syncResult.matched.length > 0 && (
                  <div>
                    <div className="text-muted-foreground">Mapped:</div>
                    <ul className="mt-1 space-y-0.5">
                      {syncResult.matched.map((m) => (
                        <li key={m.crmUserId} className="text-foreground">
                          <span className="font-medium">{m.crmEmail}</span> → Aircall user #{m.aircallUserId}
                          {m.aircallNumberE164 ? ` (${m.aircallNumberE164})` : ""}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {syncResult.unmatchedAircall.length > 0 && (
                  <div>
                    <div className="text-amber-700">Aircall users with no CRM broker (create the broker in Users first):</div>
                    <ul className="mt-1 space-y-0.5">
                      {syncResult.unmatchedAircall.map((u) => (
                        <li key={u.aircallUserId} className="text-foreground">
                          {u.aircallName ?? "(no name)"} — {u.aircallEmail ?? "(no email)"}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {syncResult.unmatchedBrokers.length > 0 && (
                  <div>
                    <div className="text-amber-700">CRM brokers with no Aircall user (their Aircall email may differ):</div>
                    <ul className="mt-1 space-y-0.5">
                      {syncResult.unmatchedBrokers.map((b) => (
                        <li key={b.id} className="text-foreground">
                          {b.fullName} — {b.email}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </div>

          {!webhookConfigured && (
            <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900">
              <strong>AIRCALL_WEBHOOK_SECRET</strong> is not set in Cloudflare. Run{" "}
              <code>wrangler secret put AIRCALL_WEBHOOK_SECRET --env production</code>{" "}
              before enabling the webhook in Aircall.
            </div>
          )}

          {testResult && (
            <div
              className={`rounded-md border p-2 text-xs ${
                testResult.startsWith("✓")
                  ? "border-emerald-200 bg-emerald-50 text-emerald-900"
                  : "border-destructive/40 bg-destructive/5 text-destructive"
              }`}
            >
              {testResult}
            </div>
          )}

          {canEdit && (
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => test.mutate()}
                disabled={test.isPending}
              >
                {test.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                Test connection
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  if (confirm("Disconnect Aircall? Existing call logs stay.")) disconnect.mutate()
                }}
                className="hover:text-destructive"
              >
                <Trash2 className="h-4 w-4" />
                Disconnect
              </Button>
            </div>
          )}
        </div>
      ) : (
        <div className="mt-4 space-y-3">
          <p className="text-sm text-muted-foreground">
            Not connected. Add your Aircall API credentials to start logging calls.
          </p>
          {canEdit && (
            <>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div>
                  <Label>Aircall API ID</Label>
                  <Input value={apiId} onChange={(e) => setApiId(e.target.value)} placeholder="XXXXXXXX" />
                </div>
                <div>
                  <Label>Aircall API Token</Label>
                  <Input
                    type="password"
                    value={apiToken}
                    onChange={(e) => setApiToken(e.target.value)}
                    placeholder="••••••"
                  />
                </div>
              </div>
              <div>
                <Label>Workspace name (optional)</Label>
                <Input
                  value={workspaceName}
                  onChange={(e) => setWorkspaceName(e.target.value)}
                  placeholder="Rivercity Finance"
                />
              </div>
              <p className="text-muted-foreground text-xs">
                Get these from Aircall → Integrations → API Keys.
              </p>
              {error && (
                <div className="border-destructive/40 bg-destructive/5 text-destructive rounded-md border p-2 text-sm">
                  {error}
                </div>
              )}
              <Button
                onClick={() => connect.mutate()}
                disabled={connect.isPending || !apiId || !apiToken}
              >
                {connect.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                Connect Aircall
              </Button>
            </>
          )}
        </div>
      )}
    </Card>
  )
}

// ============================================================
// Outlook (org config check)
// ============================================================
function OutlookOrgCard({
  status,
  isLoading,
}: {
  status: Awaited<ReturnType<typeof integrationsApi.status>> | null
  isLoading: boolean
}) {
  const outlookConfigured =
    status?.envConfigured.outlook.clientId && status?.envConfigured.outlook.tenantId

  return (
    <Card className="p-6">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="grid h-10 w-10 place-items-center rounded-lg bg-blue-100 text-blue-700">
            <Mail className="h-5 w-5" />
          </div>
          <div>
            <h3 className="text-base font-semibold text-foreground">Outlook (Microsoft 365)</h3>
            <p className="text-muted-foreground text-sm">
              Sync broker mailboxes to contact timelines via OAuth.
            </p>
          </div>
        </div>
        <StatusPill
          loading={isLoading}
          connected={!!outlookConfigured}
          label={outlookConfigured ? "Ready" : "Not configured"}
        />
      </div>

      <div className="mt-4 space-y-3 text-sm">
        {outlookConfigured ? (
          <p className="text-muted-foreground">
            OAuth is set up on the server. Each broker connects their own mailbox from{" "}
            <strong>My Settings</strong>.
          </p>
        ) : (
          <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900">
            <p className="mb-2 font-medium">To enable Outlook, an admin must set these Wrangler secrets:</p>
            <pre className="text-[11px]">wrangler secret put OUTLOOK_CLIENT_ID
wrangler secret put OUTLOOK_CLIENT_SECRET
wrangler secret put OUTLOOK_TENANT_ID</pre>
            <p className="mt-2">
              Register a Microsoft Entra app with delegated permissions{" "}
              <code>Mail.Read</code> + <code>offline_access</code> + <code>User.Read</code>, then
              add redirect URI <code>{typeof window !== "undefined" ? `${window.location.origin}/api/integrations/outlook/callback` : ""}</code>.
            </p>
          </div>
        )}
      </div>
    </Card>
  )
}

// ============================================================
// Shared pill
// ============================================================
function StatusPill({
  loading,
  connected,
  label,
}: {
  loading: boolean
  connected: boolean
  label?: string
}) {
  if (loading) {
    return (
      <Badge variant="muted" className="text-xs">
        <Loader2 className="mr-1 h-3 w-3 animate-spin" />
        Loading…
      </Badge>
    )
  }
  if (connected) {
    return (
      <Badge variant="success" className="text-xs">
        <CheckCircle2 className="mr-1 h-3 w-3" />
        {label ?? "Connected"}
      </Badge>
    )
  }
  return (
    <Badge variant="muted" className="text-xs">
      <XCircle className="mr-1 h-3 w-3" />
      {label ?? "Not connected"}
    </Badge>
  )
}
